// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import updateSearchField from "@steiner/App/Handlers/CustomerPartNumber/UpdateSearchField";
import { Draft } from "immer";

export interface CustomerProductModel {
    customerPartNumber?: string;
    productUri?: string;
    productDescription?: string;
    productErpNumber?: string;
    isVisible?: boolean;
}

export type CustomerPartNumbersState = {
    customerPartNumber?: string;
    customerProducts?: CustomerProductModel[];
    isLoading?: boolean;
    pagination: PaginationModel | null;
};

const initialState: CustomerPartNumbersState = {
    customerPartNumber: "",
    isLoading: false,
    pagination: null,
};

const xCustomerPartNumbers = createTypedReducerWithImmer(initialState, {
    "xCustomerPartNumbers/BeginLoadingCustomerPartNumbers": (
        draft: Draft<CustomerPartNumbersState>,
        action: { isLoading: boolean },
    ) => {
        draft.isLoading = action.isLoading;
    },
    "xCustomerPartNumbers/FailedCustomerPartNumbers": (draft: Draft<CustomerPartNumbersState>) => {
        draft.isLoading = false;
    },
    "xCustomerPartNumbers/CompleteLoadingCustomerPartNumbers": (
        draft: Draft<CustomerPartNumbersState>,
        action: { value: CustomerPartNumbersState },
    ) => {
        draft.customerProducts = action.value.customerProducts;
        draft.pagination = action.value.pagination;
        draft.isLoading = false;
    },

    "xCustomerPartNumbers/DeleteCustomerPartNumber": (
        draft: Draft<CustomerPartNumbersState>,
        action: { customerPartNumber: string; productNumber: string },
    ) => {
        const newCustomerProduct = draft.customerProducts?.filter(
            item =>
                item.customerPartNumber !== action.customerPartNumber && item.productErpNumber !== action.productNumber,
        );
        draft.customerProducts = newCustomerProduct;
        draft.isLoading = false;
    },
    "xCustomerPartNumbers/UpdateCustomerPartNumber": (
        draft: Draft<CustomerPartNumbersState>,
        action: { customerPartNumber: string; productNumber: string },
    ) => {
        const newCustomerProduct = draft.customerProducts?.map(item => {
            if (item.productErpNumber === action.productNumber) {
                return { ...item, customerPartNumber: action.customerPartNumber };
            }
            return item;
        });
        draft.customerProducts = newCustomerProduct;
        draft.isLoading = false;
    },
    "xCustomerPartNumbers/UpdateSearchField": (
        draft: Draft<CustomerPartNumbersState>,
        action: { customerPartNumber?: string },
    ) => {
        draft.customerPartNumber = action.customerPartNumber;
    },
});

(reducers as any).xCustomerPartNumbers = xCustomerPartNumbers;

export type xCustomerPartNumbersState = {
    xCustomerPartNumbers: CustomerPartNumbersState;
} & ApplicationState;

type CustomerPartNumbersReducer = {
    customerPartNumberData: typeof xCustomerPartNumbers;
};

export type CustomerPartNumbersActions =
    | Parameters<CustomerPartNumbersReducer[keyof CustomerPartNumbersReducer]>[1]
    | AnyAction;
